/* eslint-disable no-magic-numbers -- screen-space hit testing and NDC conversion */
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import {
  allowInput,
  controlsReleased,
  type PointerInput,
} from '@technology/web3d/dist/esnext/InputHandler.js';
import { loadPointIconTexture } from '@technology/web3d/dist/esnext/Helpers/utils.js';
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  Points,
  PointsMaterial,
  Vector2,
  Vector3,
} from '@technology/web3d-plugin-threejs';
import { EMPTY, of, Subscription } from 'rxjs';
import {
  filter,
  finalize,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  type DragConstraintAnchor,
  isDragConstrained,
  resolveConstrainedDragPoint,
  type TextDragConstraintSettings,
} from './drag-constraints';
import { isCursorNearInsertionPointHandle } from './insertion-point-hit-test';

const HANDLE_POINT_SIZE = 12;
const HANDLE_COLOR = '#ff00ff';
const CURSOR_HIT_THRESHOLD_PX = 20;

export type InsertionPointChangeListener = (position: Vector3) => void;

export type GetDragConstraintSettings = () => TextDragConstraintSettings;

export class InsertionPointHandle {
  private readonly root = new Group();
  private readonly points: Points;
  private readonly position = new Vector3();
  private readonly listener: Subscription | undefined;
  private disposed = false;

  constructor(
    private readonly api: Api,
    position: Vector3,
    private readonly getDragConstraintSettings: GetDragConstraintSettings,
    private readonly onChange: InsertionPointChangeListener,
    private readonly onDragEnd: InsertionPointChangeListener
  ) {
    this.position.copy(position);

    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new Float32BufferAttribute([position.x, position.y, position.z], 3)
    );

    const material = new PointsMaterial({
      size: HANDLE_POINT_SIZE,
      sizeAttenuation: false,
      map: loadPointIconTexture(`${api.staticRootUrl}images/icon_bg.png`),
      depthTest: false,
      transparent: true,
      color: new Color(HANDLE_COLOR),
    });

    this.points = new Points(geometry, material);
    this.points.renderOrder = 6;
    this.root.add(this.points);

    let isDragging = false;
    let dragStarted = false;
    let dragConstraintAnchor: DragConstraintAnchor | null = null;

    const addSnappedIntersection = async (
      event: PointerInput
    ): Promise<PointerInput> => {
      const settings = this.getDragConstraintSettings();
      if (isDragConstrained(settings) && dragConstraintAnchor !== null) {
        return event;
      }

      event.intersection = await this.api.picker.pickSnapped(
        new Vector2(event.x, event.y),
        this.api.settingsDispatcher.settings.snapTypes
      );
      return event;
    };

    if (this.api.eventDispatcher && this.api.inputHandler) {
      const inputHandler = this.api.inputHandler;
      const downCallback = (): void => {
        isDragging = true;
        dragStarted = false;
        const settings = this.getDragConstraintSettings();
        if (isDragConstrained(settings)) {
          dragConstraintAnchor = {
            lockedX: this.position.x,
            lockedY: this.position.y,
            lockedZ: this.position.z,
          };
        }
      };

      const moveCallback = (event: PointerInput): void => {
        if (!isDragging) {
          return;
        }

        dragStarted = true;

        let dragPoint: Vector3 | null = null;
        if (dragConstraintAnchor !== null) {
          dragPoint = resolveConstrainedDragPoint(
            this.api,
            event.x,
            event.y,
            dragConstraintAnchor,
            this.getDragConstraintSettings()
          );
        } else if (event.intersection?.point) {
          dragPoint = event.intersection.point;
        }

        if (dragPoint) {
          this.setPosition(dragPoint);
          this.onChange(this.position);
          this.api.renderingManager.redraw();
        }
      };

      const upCallback = (): void => {
        this.api.eventDispatcher?.dragEnd(undefined);
        if (isDragging) {
          if (dragStarted) {
            this.onDragEnd(this.position.clone());
          }
          isDragging = false;
          dragStarted = false;
          dragConstraintAnchor = null;
        }
      };

      const options = { button: 0, touchCount: 1 };
      const camera = this.api.camera;
      const canvas = this.api.renderingManager.renderer.domElement;

      this.listener = inputHandler.pointerDown$
        .pipe(
          filter(allowInput(options)),
          tap((event) => {
            if (
              isCursorNearInsertionPointHandle(
                event,
                this.position,
                camera,
                canvas,
                CURSOR_HIT_THRESHOLD_PX
              )
            ) {
              this.api.eventDispatcher?.dragStart(undefined);
            }
          }),
          mergeMap((event) => {
            if (
              isCursorNearInsertionPointHandle(
                event,
                this.position,
                camera,
                canvas,
                CURSOR_HIT_THRESHOLD_PX
              )
            ) {
              return of(event);
            }
            this.api.eventDispatcher?.dragEnd(undefined);
            return EMPTY;
          }),
          tap(() => {
            downCallback();
          }),
          switchMap(() =>
            inputHandler.pointerMove$.pipe(
              takeUntil(controlsReleased(inputHandler, options)),
              switchMap((event) =>
                addSnappedIntersection(event).catch(() => event)
              ),
              tap(moveCallback),
              finalize(upCallback)
            )
          )
        )
        .subscribe({
          error: () => {
            upCallback();
          },
        });
    }
  }

  public get object3D(): Group {
    return this.root;
  }

  public setPosition(position: Vector3): void {
    this.position.copy(position);
    const attribute = this.points.geometry.getAttribute('position');
    if (attribute) {
      attribute.setXYZ(0, position.x, position.y, position.z);
      attribute.needsUpdate = true;
    }
    this.points.geometry.computeBoundingSphere();
  }

  public dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.listener?.unsubscribe();
    this.points.geometry.dispose();
    const material = this.points.material;
    if (material instanceof PointsMaterial) {
      material.map?.dispose();
      material.dispose();
    }
    this.root.remove(this.points);
  }
}
